/**
 * @file lineUnitRequired.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-28
 * @description Units are required on customer order lines (Cam, 28 Oct 2026 —
 *              "Can we make units a mandatory field when creating customer
 *              orders please"). One rule for Create / Edit Customer Order and
 *              the order save (POST / PUT /api/orders/customer-orders, 400
 *              UNIT_REQUIRED).
 *
 *   A LINE'S UNIT IS KNOWN when it has one of its own (a unit weight, or None
 *   — 0, no weight), or its item has one (a unit weight above 0), or its item
 *   is on a bulk sales code (201 / 202 / 204 / 206 / 209), which the whole
 *   system already counts per tonne (utils/tonnage, utils/itemCostUnit).
 *   A line of an item with no unit and no bulk code — or a typed line with no
 *   item — has to be given one.
 *
 *   WHICH LINES (tracker q02, Cam: "New and changed lines only"). A new line
 *   must have a known unit. On an edit, a line already on the order is asked
 *   only when this save changes its item, its quantity or its units — an old
 *   order whose lines were saved without a unit still saves until those lines
 *   are cleaned up (scripts/report-lines-without-units.js).
 *
 *   EXPLICIT ON PICKING. Choosing an item puts the item's unit on the line
 *   (explicitUnitKg): its tote for a tote item, else the item's own unit
 *   weight, else a tonne for a bulk-coded item — so the line carries its unit
 *   rather than leaning on the item's, and a later change to the item cannot
 *   quietly change what the line's quantity means.
 *
 *   The server's utils/lineUnitRequired.js is GENERATED from this file by
 *   scripts/sync-shared-utils.js — edit this one and re-run it.
 */

import { isNoWeight } from './lineUnits';
import { defaultLineWeightKg, COST_BULK_SALES_CODES, isToteItem, MILK_POWDER_SALES_CODE } from './itemCostUnit';

/** The save's refusal code. */
export const UNIT_REQUIRED_CODE = 'UNIT_REQUIRED';

const blank = (v) => v === '' || v === null || v === undefined || String(v).trim() === '';
const positive = (v) => {
  if (blank(v)) return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};
const codeOf = (item) => String(item?.sales_account_code ?? item?.salesAccountCode ?? '').trim().toUpperCase();
const weightOf = (o) => o?.unit_weight_kg ?? o?.unitWeightKg;

/** The line has a unit of its own: a weight, or None (0). */
export function hasOwnUnit(line) {
  const w = weightOf(line);
  return isNoWeight(w) || positive(w) !== null;
}

/**
 * The unit the item gives a line, kg — 0 is never returned (an item is
 * never "None"). null = the item gives no unit (choose one).
 */
export function itemUnitKg(item) {
  if (!item) return null;
  const own = positive(weightOf(item));
  if (own !== null) return own;
  if (codeOf(item) === MILK_POWDER_SALES_CODE) return null;
  if (isToteItem(item)) return 1000;
  if (COST_BULK_SALES_CODES.includes(codeOf(item))) return 1000;
  return null;
}

/** Is this line's unit known (its own, else its item's)? */
export function lineUnitKnown(line, item = null) {
  return hasOwnUnit(line) || itemUnitKg(item) !== null;
}

/**
 * What picking this item puts in the line's Units: its tote (a tote item
 * costed per tonne), else the item's unit. null = nothing to put (choose one).
 */
export function explicitUnitKg(item) {
  if (!item) return null;
  return defaultLineWeightKg(item) ?? itemUnitKg(item);
}

const num = (v) => (blank(v) ? null : (Number.isFinite(Number(v)) ? Number(v) : null));
const sameCode = (a, b) => String(a ?? '').trim().toUpperCase() === String(b ?? '').trim().toUpperCase();
const sameNum = (a, b) => num(a) === num(b);

const storedOf = (stored, id) => {
  if (!stored || id === null || id === undefined) return undefined;
  if (typeof stored.get === 'function') return stored.get(id) ?? stored.get(Number(id)) ?? stored.get(String(id));
  return stored[id];
};

/**
 * Does this save change the line's item, quantity or units? A line with no
 * stored copy (new) counts as changed.
 * @param {object} line   { inventory_item_id, product_code, quantity, unit_weight_kg }
 * @param {object|undefined} before the line as stored
 */
export function lineChangedForUnits(line, before) {
  if (!before) return true;
  if (!sameNum(line.inventory_item_id, before.inventory_item_id)) return true;
  if (!sameCode(line.product_code, before.product_code)) return true;
  if (!sameNum(line.quantity, before.quantity)) return true;
  // A unit set or changed is a change; units left as they were are not.
  const a = weightOf(line);
  const b = weightOf(before);
  if (blank(a) !== blank(b)) return true;
  if (!blank(a) && !sameNum(a, b)) return true;
  return false;
}

/**
 * The lines a save must refuse for having no known unit.
 * @param {Array<object>} lines   the lines being saved
 * @param {Map|object|null} stored the order's lines as stored — id → { inventory_item_id,
 *        product_code, quantity, unit_weight_kg } (an edit); null for a new order
 * @param {(line) => object|null} itemOf the line's inventory item ({ unit_weight_kg, sales_account_code, item_name … })
 * @returns {Array<{ index, id, label, text }>}
 */
export function lineUnitRefusals(lines, stored = null, itemOf = () => null) {
  const out = [];
  (Array.isArray(lines) ? lines : []).forEach((line, index) => {
    if (!line) return;
    const before = storedOf(stored, line.id);
    if (!lineChangedForUnits(line, before)) return;
    if (lineUnitKnown(line, itemOf(line) || null)) return;
    const name = [line.product_code, line.product_name].map(s => String(s || '').trim()).filter(Boolean).join(' ');
    const label = `Line ${index + 1}${name ? ` (${name})` : ''}`;
    out.push({ index, id: line.id ?? null, label, text: `${label} has no unit` });
  });
  return out;
}

/** The words a line's Units cell flags. */
export const UNIT_PROBLEM_TEXT = 'Choose a unit — the item has none (Bulk, a tote, a bag, Other or None)';

/** One sentence for a refusal, or '' when there is none. */
export function unitRefusalMessage(refusals) {
  if (!refusals || !refusals.length) return '';
  const list = refusals.map(r => r.text);
  const joined = list.length === 1 ? list[0] : `${list.slice(0, -1).join('; ')} and ${list[list.length - 1]}`;
  return `${joined} — choose its unit in the Units column${refusals.length > 1 ? ' of each' : ''} (a bulk tonne, a tote, a bag, Other by the pallet, or None for a charge with no weight).`;
}
